import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShieldCheck } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setError("Missing invite token"); setLoading(false); return; }
    api.get(`/family/invite/${token}`)
      .then((r) => { setInvite(r.data); if (r.data.email) setForm((f) => ({ ...f, email: r.data.email })); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/family/invite/accept", { token, ...form });
      // Force full reload so AuthContext re-fetches /auth/me with the new cookie.
      window.location.assign("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F5F4F0]">
      <div className="w-full max-w-md card-soft p-8 md:p-10 fade-in-up" data-testid="accept-invite-page">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5C7A4F] text-white mb-4">
          <ShieldCheck size={22} />
        </div>
        <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">Join the family</h2>
        {loading ? (
          <p className="text-[#707A6E] mt-3">Checking your invite…</p>
        ) : invite ? (
          <>
            <p className="text-[#707A6E] mt-1 mb-8">
              {invite.invited_by_name || "A parent"} invited you to co-manage your kids on Yoda Watch.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Your name</label>
                <input required data-testid="invite-name-input" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Email</label>
                <input required type="email" data-testid="invite-email-input" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Password</label>
                <input required type="password" minLength={6} data-testid="invite-password-input" value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
              </div>
              {error && <div className="text-sm text-[#C06E52] bg-[#C06E52]/10 rounded-xl px-3 py-2" data-testid="invite-error">{error}</div>}
              <button data-testid="invite-submit-btn" disabled={submitting}
                      className="w-full rounded-xl bg-[#5C7A4F] text-white px-6 py-3 font-medium hover:bg-[#4B6640] disabled:opacity-60">
                {submitting ? "Joining…" : "Join family"}
              </button>
            </form>
          </>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-[#C06E52] bg-[#C06E52]/10 rounded-xl px-3 py-2" data-testid="invite-error">{error || "Invite not found"}</p>
            <Link to="/login" className="mt-4 inline-block text-[#5C7A4F] hover:underline">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
